import { computed } from 'vue';
import { FuelDataType } from '../foundation/interfaces/datasources/IDataSourceProps';
import { storeToRefs } from 'pinia';
import { useContentStore } from '../foundation/stores/contentStore';
import { sitecoreVehicleType } from '../foundation/helpers';

interface IRunningCostsOptions {
  isSearchResult?: boolean;
  costToFillLabel?: string;
  costPer100KmLabel?: string;
}

/**
 * Composable to obtain vehicle running costs.
 * @param carData
 * @returns
 */
export default function useRunningCosts(carData: any, { isSearchResult = false, costToFillLabel, costPer100KmLabel }: IRunningCostsOptions) {
  const contentStore = useContentStore();
  const { fuelPrices } = storeToRefs(contentStore);

  const costToRunText = computed(() =>
    costToRunData?.value?.COST_PER_100KM?.value !== 'N/A' ? `${costToRunData.value.COST_PER_100KM.value} per 100km` : 'Data not available'
  );

  /**
   * Calculate the fuel price based on fuel data from API.
   * If electric, go straight to KWH.
   *
   * If hasOctaneValue, go straight to calculation.
   *
   * If DIESEL, use diesel default.
   *
   * Otherwise check for petrol and use default fuel
   */
  const calculatedFuelPrice = computed(() => {
    const DEFAULT_FUEL = 'RULP91';
    const ELECTRIC = 'KWH';
    const DIESEL_FUEL = 'Diesel';

    const vehicleType = sitecoreVehicleType(carData, isSearchResult);
    const fuelOctane: string = (isSearchResult ? carData.fuelOctane : carData.Primary_engine_motor_Fuel_octane)?.toString()?.toLowerCase();
    const fuelType: string = (
      isSearchResult ? carData.fuelType : carData.Primary_engine_fuel_type || carData.Primary_engine_motor_Fuel_type || carData.Fuel_type
    )?.toLowerCase();

    // If we know the octane value then use that and return the result.
    if (Number(fuelOctane)) {
      let result = fuelPrices.value.fuelPriceData.find((fuel) => extractNumericOctaneValueFromFuelLabel(fuel) === fuelOctane)?.fuelPrice;
      return Number(result);
    }

    // EVs
    if (fuelType === 'electric' || fuelType === 'electricity' || vehicleType === 'BEV') {
      return fuelPrices.value.fuelPriceData.find((fuel) => fuel.fuelType === ELECTRIC)?.fuelPrice;
    }

    // Diesel
    if (fuelType === 'diesel') {
      return fuelPrices.value.fuelPriceData.find((fuel) => fuel.fuelType === DIESEL_FUEL)?.fuelPrice;
    }

    // Fallback to petrol default
    if (fuelType === 'petrol') {
      return fuelPrices.value.fuelPriceData.find((fuel) => fuel.fuelType === DEFAULT_FUEL)?.fuelPrice;
    }

    return 0;
  });

  const costToRunData = computed(() => {
    const fuelConsumptionPer100km = isSearchResult ? carData?.fuelEconomy : carData.Fuel_consumption_Combined_ADR8102_lt_100km;
    const powerConsumptionPer100km = isSearchResult ? carData?.powerConsumption : carData.Power_consumption_Combined_kWh_100km;
    const vehicleType = sitecoreVehicleType(carData, isSearchResult);
    const fuelType: string = (
      isSearchResult ? carData.fuelType : carData.Primary_engine_fuel_type || carData.Primary_engine_motor_Fuel_type || carData.Fuel_type
    )?.toLowerCase();

    let costPerFill = undefined;
    let costPer100km;

    if (calculatedFuelPrice.value) {
      const fuelPrice = calculatedFuelPrice.value as number;

      if (fuelPrice > 0) {
        if (fuelType === 'electric' || fuelType === 'electricity' || vehicleType === 'BEV') {
          costPerFill = (Number(carData.Battery_Capacity_kWh) * fuelPrice) / 100;
        } else {
          costPerFill = (Number(carData.Fuel_tank_capacity_Primary_lt) * fuelPrice) / 100;
        }

        if (vehicleType == 'PHEV' && (!fuelConsumptionPer100km || !powerConsumptionPer100km)) {
          // DSQ-601
          // Display N/A if vehicle is PHEV and both fuelConsumptionPer100km and powerConsumptionPer100km are not populated
          costPer100km = 'N/A';
        } else if (powerConsumptionPer100km && Number(powerConsumptionPer100km) > 0) {
          costPer100km = roundedAndFixedDollarValue((Number(powerConsumptionPer100km) * fuelPrice) / 100);
        } else if (fuelConsumptionPer100km && Number(fuelConsumptionPer100km) > 0) {
          costPer100km = roundedAndFixedDollarValue((Number(fuelConsumptionPer100km) * fuelPrice) / 100);
        } else {
          costPer100km = 'N/A';
        }
      }
    }

    return {
      COST_PER_FILL: {
        label: costToFillLabel,
        value: costPerFill ? roundedAndFixedDollarValue(costPerFill) : 'N/A',
      },
      COST_PER_100KM: {
        label: costPer100KmLabel,
        value: costPer100km || 'N/A',
      },
    };
  });

  return { costToRunText, costToRunData };
}

// The fuel types are string values such as 'RULP91' or 'PULP98'.
// This function extracts the digit portion of the string,
// which is used to compare against the primary engine
// motor fuel octane value from 'carData'.
function extractNumericOctaneValueFromFuelLabel(fuel: FuelDataType) {
  return fuel.fuelType.replace(/\D+/, '');
}

function roundedAndFixedDollarValue(val: number | string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'AUD',
    currencyDisplay: 'narrowSymbol',
    currencySign: 'accounting',
    maximumFractionDigits: 2,
    // @ts-ignore (experimental support)
    roundingIncrement: 5,
  }).format(Number(val));
}
